import { createSelector } from '@reduxjs/toolkit';

import selectSelf from 'shared/utils/selectSelf';
import searchBy from 'shared/utils/searchBy';
import formatDate from 'shared/utils/formatDate';
import processTableFilterOptions from 'shared/utils/processTableFilterOptions';
import { CaseStatuses } from 'utils/enums';

import { keyOrdersApi } from './api';
import type { KeyOrderCase } from './types';

type KeyOrderStatus = KeyOrderCase['Status'];

function getUniqueStatuses(keyOrders: KeyOrderCase[]) {
  const statusesMap: Map<CaseStatuses, NonNullable<KeyOrderStatus>> = new Map();

  for (const keyOrder of keyOrders) {
    if (!keyOrder.Status) continue;

    statusesMap.set(keyOrder.Status.Value, keyOrder.Status);
  }

  return Array.from(statusesMap.values());
}

function getUniqueYearsList(keyOrders: KeyOrderCase[]) {
  const set = new Set<number>();

  for (const keyOrder of keyOrders) {
    if (!keyOrder.CreatedOn) continue;

    const year = new Date(keyOrder.CreatedOn).getFullYear();

    set.add(year);
  }

  return Array.from(set);
}

const responseSelector = keyOrdersApi.endpoints.getKeyOrdersList.select();

const selectResponse = createSelector(selectSelf, responseSelector);

const emptyKeyOrdersList: KeyOrderCase[] = [];

export const selectSortParams = createSelector(
  selectSelf,
  (state) => state.keyOrders.sortParams
);

const selectCooperativeId = createSelector(
  selectSelf,
  (state) => state.settings.current_cooperative
);

const selectKeyOrdersList = createSelector(
  selectResponse,
  (response) => response.data ?? emptyKeyOrdersList
);

export const selectSearchTerm = createSelector(
  selectSelf,
  (state) => state.keyOrders.searchTerm
);

const selectCooperativeRelatedKeyOrdersList = createSelector(
  selectCooperativeId,
  selectKeyOrdersList,
  (coopId, list) => list.filter(({ Cooperative }) => Cooperative?.Id === coopId)
);

export const selectStatusOptions = createSelector(
  selectCooperativeRelatedKeyOrdersList,
  (list) => processTableFilterOptions(getUniqueStatuses(list))
);

export const selectYearOptions = createSelector(
  selectCooperativeRelatedKeyOrdersList,
  (list) =>
    processTableFilterOptions(getUniqueYearsList(list), { sortOrder: 'desc' })
);

export const selectFilteredBySearchTermKeyOrdersList = createSelector(
  selectCooperativeRelatedKeyOrdersList,
  selectSearchTerm,
  (keyOrdersList, searchTerm) =>
    searchBy(
      keyOrdersList,
      [
        (keyOrder) => keyOrder.Title ?? '',
        (caseItem) => caseItem.Space?.Name ?? '',
        (caseItem) => caseItem.Status?.Label ?? '',
        (caseItem) =>
          caseItem.CreatedOn ? formatDate(new Date(caseItem.CreatedOn)) : '',
        (caseItem) =>
          caseItem.ModifiedOn ? formatDate(new Date(caseItem.ModifiedOn)) : '',
      ],
      searchTerm
    )
);
